import { createRoot } from 'react-dom/client';
import { Capacitor } from '@capacitor/core';
import App from './App.tsx';
import './index.css';
import './i18n';

// =============================================================================
// Type Declarations
// =============================================================================

declare global {
  interface Window {
    NativelyInfo?: new () => {
      browserInfo: () => { isNativeApp: boolean; osName?: string };
      getAppInfo: (callback: (resp: { osName: string }) => void) => void;
    };
    __NATIVE_DETECTION__?: { isNative: boolean; platform: 'ios' | 'android' | null };
    __detectNative?: () => void;
  }
}

// =============================================================================
// Native App Detection - Checks Capacitor first, then BuildNatively/sessionStorage
// =============================================================================

const detectNativeApp = (): { isNative: boolean; platform: 'ios' | 'android' | null } => {
  // Check Capacitor first (Linus builds use Capacitor)
  try {
    if (Capacitor.isNativePlatform()) {
      const platform = Capacitor.getPlatform();
      if (platform === 'ios' || platform === 'android') {
        console.log('[main.tsx] Capacitor native detected:', platform);
        return { isNative: true, platform };
      }
    }
  } catch (e) {
    // Capacitor not available or not native
  }

  // Fallback: Check sessionStorage (may be set by BuildNatively SDK)
  try {
    const storedContainer = sessionStorage.getItem('native_container');
    if (storedContainer === 'natively') {
      const storedPlatform = sessionStorage.getItem('native_platform') as 'ios' | 'android' | null;
      console.log('[main.tsx] BuildNatively detected from sessionStorage:', storedPlatform);
      return { isNative: true, platform: storedPlatform };
    }
    
    // If BuildNatively SDK loaded, run detection now
    if (window.NativelyInfo) {
      window.__detectNative?.();
      const platform = sessionStorage.getItem('native_platform') as 'ios' | 'android' | null;
      if (sessionStorage.getItem('native_container') === 'natively') {
        console.log('[main.tsx] BuildNatively SDK detected:', platform);
        return { isNative: true, platform };
      }
    }
  } catch (e) {}

  return { isNative: false, platform: null };
};

// =============================================================================
// RUN DETECTION SYNCHRONOUSLY BEFORE REACT RENDERS
// =============================================================================

const detection = detectNativeApp();

// Store on window for React component access
window.__NATIVE_DETECTION__ = detection;

// Add native app classes to body for CSS targeting
if (detection.isNative) {
  document.body.classList.add('native-app');
  if (detection.platform) {
    document.body.classList.add(`native-app-${detection.platform}`);
  }

  // Native builds should always load the newest hosted web bundle (avoid stale SW cache in WebView)
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations()
      .then((registrations) => Promise.all(registrations.map((registration) => registration.unregister())))
      .catch((error) => console.warn('[main.tsx] Failed to unregister service workers in native app:', error));
  }

  if ('caches' in window) {
    caches.keys()
      .then((keys) => Promise.all(keys.map((key) => caches.delete(key))))
      .catch((error) => console.warn('[main.tsx] Failed to clear caches in native app:', error));
  }
}

console.log('[main.tsx] Final native detection:', detection);

// =============================================================================
// RENDER REACT APP
// =============================================================================

createRoot(document.getElementById("root")!).render(<App />);
